import React from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';

import { TimelineEditMode } from '../contexts/PreferencesContext';
import { useBackfillTimelinePreferences } from '../hooks/useBackfillTimelinePreferences';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const SNAP_CHOICES = [1, 5, 10, 15, 30];

const EDIT_MODE_HELP: Record<TimelineEditMode, string> = {
  ripple:
    'Dragging an edge slides the neighbouring block, keeping its length. Unaccounted gaps absorb the change first, so the rest of your day usually stays put.',
  roll: 'Dragging an edge holds the neighbouring block in place and shortens it instead. Nothing else on the timeline moves.',
};

/**
 * Profile → Backfill Timeline: the account-level defaults for the backfill
 * wizard's draggable timeline.
 *
 * `edit_mode` is only the *default*; the wizard offers a per-gesture override
 * while you drag, so switching for one awkward block never means coming back
 * here. That is why nothing is stored per block.
 */
const BackfillTimelineSettingsSection: React.FC = () => {
  const { cardContentPaddingSx } = useLayoutTokens();
  const { settings, setEditMode, setSnapMinutes, setShowTimeline } =
    useBackfillTimelinePreferences();

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Backfill Timeline
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How the backfill wizard behaves when you drag a block&apos;s edge to change
          when it started or ended.
        </Typography>

        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="backfill-edit-mode-label">Edge drag behaviour</InputLabel>
            <Select
              labelId="backfill-edit-mode-label"
              label="Edge drag behaviour"
              value={settings.edit_mode}
              onChange={(event) => setEditMode(event.target.value as TimelineEditMode)}
            >
              <MenuItem value="ripple">Ripple — move the neighbour</MenuItem>
              <MenuItem value="roll">Roll — shorten the neighbour</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {EDIT_MODE_HELP[settings.edit_mode]}
          </Typography>
        </Box>

        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="backfill-snap-label">Snap to</InputLabel>
            <Select
              labelId="backfill-snap-label"
              label="Snap to"
              value={settings.snap_minutes}
              onChange={(event) => setSnapMinutes(Number(event.target.value))}
            >
              {SNAP_CHOICES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value === 1 ? 'Every minute' : `${value} minutes`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Edges also snap to the start and end of entries you have already tracked,
            whichever is closer.
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={settings.show_timeline}
              onChange={(event) => setShowTimeline(event.target.checked)}
            />
          }
          label="Show the timeline while walking blocks"
        />
      </CardContent>
    </Card>
  );
};

export default BackfillTimelineSettingsSection;
